import React from 'react';
import { LogOut } from 'lucide-react';
import { visibleNavGroups, withNavLabelOverrides, isRouteActive } from './navConfig';

// ============================================================================
// CHA-240 — Carbon shell Sidebar (LIGHT theme, operator ratification #1)
// CHA-272 — tenant-branded header, live Agents badge, footer sign-out
// CHA-273 — `variant="dark"` (Ops concept): ink chrome on the Agents route ONLY
// ============================================================================
// Fixed 256px sidebar (no collapse — operator ratification #4). Chrome only:
// navigateTo + route ids are the legacy contract, untouched. Admin item is
// gated by `isAdmin`, matching the legacy `navItems` fallback. Brand
// hierarchy per the v2 design: the workspace is the TENANT (logo + name);
// ChainTo branding is reserved for the future platform bar. `agentsBadge`
// comes from the shared useAgentTickets view model so it always matches the
// Today KPI strip and the cockpit counts. `variant` defaults to 'light' — the
// dashboard passes 'dark' only when the agents route is active.
export default function Sidebar({
  currentRoute,
  navigateTo,
  isAdmin,
  user,
  userProfile,
  agentsBadge = 0,
  agentsBadgeError = false,
  onSignOut,
  variant = 'light',
  brand,
  navLabelOverrides,
}) {
  const shellBrand = {
    name: 'InMocean',
    alt: 'InMocean',
    subtitle: 'Supply Chain · Ops',
    logoSrc: '/tenants/inmocean/config/logo.png',
    ...(brand || {}),
  };
  const groups = withNavLabelOverrides(visibleNavGroups(userProfile?.pages), navLabelOverrides);
  const displayName =
    userProfile?.displayName ||
    userProfile?.name ||
    user?.displayName ||
    user?.email?.split('@')[0] ||
    'User';
  const role = userProfile?.role || 'member';
  const initial = (displayName.trim()[0] || 'U').toUpperCase();
  const dark = variant === 'dark';

  // Per-variant class sets. Dark = Ops concept: bg ink #161616, items gray-30,
  // hover/active fill #262626 (gray-90), active keeps the 3px blue left border,
  // eyebrows gray-60, footer/header borders gray-90, tenant name white.
  const cls = dark
    ? {
        container: 'bg-carbon-ink text-carbon-gray-30',
        divider: 'border-carbon-gray-90',
        tenantName: 'text-carbon-white',
        eyebrow: 'text-carbon-gray-60',
        itemActive: 'border-carbon-blue-60 bg-carbon-gray-90 text-carbon-white font-semibold',
        itemIdle: 'border-transparent text-carbon-gray-30 hover:bg-carbon-gray-90 hover:text-carbon-white',
        avatar: 'bg-carbon-gray-80 text-carbon-gray-30',
        signOut: 'text-carbon-gray-60 hover:text-carbon-white hover:bg-carbon-gray-90',
      }
    : {
        container: 'bg-carbon-white border-r border-carbon-border text-carbon-ink',
        divider: 'border-carbon-border',
        tenantName: '',
        eyebrow: 'text-carbon-gray-50',
        itemActive: 'border-carbon-blue-60 bg-carbon-gray-10 text-carbon-ink font-semibold',
        itemIdle: 'border-transparent text-carbon-gray-70 hover:bg-carbon-gray-10',
        avatar: 'bg-carbon-gray-10 text-carbon-ink',
        signOut: 'text-carbon-gray-50 hover:text-carbon-ink hover:bg-carbon-gray-10',
      };

  return (
    <div className={`w-64 ${cls.container} flex flex-col h-screen sticky top-0 flex-shrink-0 font-plex`}>
      {/* Header: tenant logo + tenant name + mono eyebrow (brand = workspace) */}
      <div className={`flex items-center gap-2.5 px-5 pt-[18px] pb-[14px] border-b ${cls.divider}`}>
        {shellBrand.logoSrc ? (
          <img src={shellBrand.logoSrc} alt={shellBrand.alt || shellBrand.name} className="h-[28px] w-auto" />
        ) : (
          <span className="text-[22px]" aria-hidden="true">{shellBrand.emoji || '⚡'}</span>
        )}
        <div>
          <div className={`text-[15px] font-semibold tracking-[0.01em] ${cls.tenantName}`}>{shellBrand.name}</div>
          <div className="font-plex-mono text-[9.5px] text-carbon-gray-50 tracking-[0.1em] uppercase">
            {shellBrand.subtitle}
          </div>
        </div>
      </div>

      {/* Grouped nav */}
      <nav aria-label="Primary" className="flex-1 overflow-y-auto pt-1.5 pb-3">
        {/* CHA-553 (A+B): nav filtered by the role's page grants. Roles with no
            explicit grant list (user/viewer) get the full nav unchanged
            (allow-by-default); admin (`['*']`) sees all; restricted roles
            (powerbi/executive/…) see only their granted sections. This
            replaces the isAdmin-only gate on admin items — visibility is now a
            single source of truth in visibleNavGroups. */}
        {groups.map((group) => (
          <div key={group.label}>
            <div
              className={`font-plex-mono text-[10px] tracking-[0.14em] ${cls.eyebrow} uppercase px-5 pt-[18px] pb-1.5`}
            >
              {group.label}
            </div>
            {group.items
              .map((item) => {
                const Icon = item.icon;
                const active = isRouteActive(currentRoute, item.id);
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => navigateTo(item.id)}
                    aria-current={active ? 'page' : undefined}
                    className={`w-full flex items-center gap-2.5 py-[9px] pr-5 pl-[17px] text-left text-[13.5px] border-l-[3px] transition-colors ${
                      active ? cls.itemActive : cls.itemIdle
                    }`}
                  >
                    <Icon size={16} />
                    <span className="flex-1">{item.label}</span>
                    {item.id === 'agents' && agentsBadgeError ? (
                      // A failed subscription must not read as "queue clear":
                      // the count is unknown, so show an explicit unknown mark.
                      <span
                        data-testid="agents-badge-error"
                        title="Agent queue unavailable"
                        aria-label="Agent queue unavailable"
                        className="font-plex-mono text-[10px] font-semibold bg-carbon-orange-40 text-carbon-white px-1.5 py-px min-w-[18px] text-center"
                      >
                        !
                      </span>
                    ) : item.id === 'agents' && agentsBadge > 0 ? (
                      <span
                        data-testid="agents-badge"
                        aria-label={`${agentsBadge} open agent tickets`}
                        className="font-plex-mono text-[10px] font-semibold bg-carbon-red-60 text-carbon-white px-1.5 py-px min-w-[18px] text-center"
                      >
                        {agentsBadge}
                      </span>
                    ) : null}
                  </button>
                );
              })}
          </div>
        ))}
      </nav>

      {/* Footer: square avatar + name + mono role + sign-out */}
      <div className={`flex items-center gap-2.5 px-5 py-[14px] border-t ${cls.divider}`}>
        <div
          className={`w-7 h-7 rounded-none ${cls.avatar} flex items-center justify-center text-xs font-semibold`}
        >
          {initial}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[12.5px] truncate">{displayName}</div>
          <div className="font-plex-mono text-[9.5px] text-carbon-gray-50 truncate">
            {role} · {shellBrand.tenantId || 'inmocean'}
          </div>
        </div>
        {onSignOut ? (
          <button
            type="button"
            onClick={onSignOut}
            title="Sign out"
            aria-label="Sign out"
            className={`p-1.5 ${cls.signOut} flex-shrink-0`}
          >
            <LogOut size={15} />
          </button>
        ) : null}
      </div>
    </div>
  );
}
